import React, { useEffect, useState, useContext } from "react";
import {
    Box, Typography, FormGroup, Checkbox,
    Grid,
    Card,
    CardContent,
    Button,
    TextField
} from "@mui/material";
import NavigationBar from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';

import CustomerReqImg from "../../images/CustomerReqImg.svg";
import axios from "axios";
import { API_CONFIG } from "../../api/apiUrl";
import type { SystemConfig } from "../../types/ContractorsProfile";
import { AuthContext } from "../../context/authcontext";
import toast from "react-hot-toast";



const EstimateSection = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const plotIdFromUrl = searchParams.get("plotId");
    const [plot, setPlot] = useState(location.state?.plot || null);
    const files = (location.state?.files && location.state.files.length > 0)
      ? location.state.files
      : (plot?.files || []);
    const services = location.state?.services || [];
    const contractor = location.state?.contractor;
    const pricingPlans = contractor?.pricingPlans || [];
    const [marginConfig, setMarginConfig] = useState<SystemConfig | null>(null);
    const [loadingMargin, setLoadingMargin] = useState(true);
    const [markUp, setMarkUp] = useState<string>("");
    const [isEditingMarkUp, setIsEditingMarkUp] = useState(false);
    const authContext = useContext(AuthContext);
    const userId = authContext?.user?.id;
    const selectedPlotId = authContext?.selectedPlotId;
    const [isLoading, setIsLoading] = useState(false);
    const [expandedPlanIndex, setExpandedPlanIndex] = useState<number | null>(null);

    // console.log(plot,"fff")

    useEffect(() => {
        if (!plot && plotIdFromUrl) {
            const token = localStorage.getItem("id_token");
            axios.get(`${API_CONFIG.PLOTS}/${plotIdFromUrl}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
            .then(res => setPlot(res.data))
            .catch(() => toast.error("Plot not found"));
        }
    }, [plot, plotIdFromUrl]);

    useEffect(() => {
        const fetchMarginConfig = async () => {
            try {
                const token = localStorage.getItem("id_token");
                const response = await axios.get(
                    `${API_CONFIG.SYSTEM_CONFIGS}?keyName.equals=MARGIN&page=0&size=20`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (response.data && response.data.length > 0) {
                    setMarginConfig(response.data[0]);
                    setMarkUp(String(response.data[0].valueNumers ?? ""));
                }
            } catch (error) {
              
            } finally {
                setLoadingMargin(false);
            }
        };
        fetchMarginConfig();
    }, []);

    const handleMarkUpChange = (e:any) => {
        const value = e.target.value.replace(/[^\d.]/g, "");
        setMarkUp(value);
    };

    const handleEditMarkUp = () => setIsEditingMarkUp(true);
    const handleSaveMarkUp = async () => {
        setIsEditingMarkUp(false);
        if (marginConfig) {
            setMarginConfig({ ...marginConfig, valueNumers: Number(markUp) });
        }
    };

    const handleSendEstimate = async () => {
        if (!userId) {
            toast.error("User not found");
            return;
        }
        const plotId = getPlotId();
        if (!plotId) {
            toast.error("Plot not found");
            return;
        }
        if (!pricingPlans.length) {
            toast.error("No pricing plans available");
            return;
        }
        if (!marginConfig) {
            toast.error("Margin config not found");
            return;
        }
        if (!contractor?.id) {
            toast.error("Company Profile ID not found");
            return;
        }
        setIsLoading(true);
        const plan = pricingPlans[0];
        const adjustedPrice = getAdjustedPrice(plan.price);
        // console.log(adjustedPrice)
        const token = localStorage.getItem("id_token");
        try {
            await axios.post(
                API_CONFIG.PLOT_RATE_CARDS,
                {
                    plotId: plotId,
                    companyProfileId: contractor.id,
                    negotiatedPrice: Number(markUp),
                    isPercent: marginConfig.isPercent
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            toast.success("Estimate has been sent! Redirecting to bookings...");
            navigate('/booking', { replace: true });
        } catch (error) {
            toast.error("Failed to send estimate");
        } finally {
            setIsLoading(false);
        }
    };

    const getAdjustedPrice = (price: number) => {
        if (marginConfig) {
            if (marginConfig.isPercent) {
                return Math.floor(price * (1 + Number(markUp) / 100));
            } else {
                return Math.floor(price + Number(markUp));
            }
        }
        return Math.floor(price);
    };


    const getPlotId = () => {
        if (plot?.id) return plot.id;
        if (plot?.plot?.id) return plot.plot.id;
        if (selectedPlotId) return selectedPlotId;
        return undefined;
    };

    const handleToggleFeatures = (planIndex: number) => {
        setExpandedPlanIndex(expandedPlanIndex === planIndex ? null : planIndex);
    };

    return (
        <>
            <Box sx={{ maxWidth: '2000px', margin: '0 auto' }}>
                <NavigationBar />
                <Box sx={{
                    py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 },
                    px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 },
                }}>
                    <Box sx={styles.mainContainer}>
                        <Box sx={styles.header}>
                            <Typography sx={styles.headerTypography}>Estimate</Typography>
                        </Box>
                        <Box sx={styles.mainBox}>
                            <Box sx={styles.borderBox}>
                                <Box sx={{ padding: { xs: 0.5, sm: 2, md: 1 } }}>
                                    <Box sx={{ maxWidth: "700px", position: "relative" }}>
                                        <Box sx={{ pt: { xs: 1, md: 2.7 }, px: { xs: 2, sm: 1, md: 3, lg: 7, xl: 10 } }}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><Typography sx={styles.label} textTransform="capitalize">Name</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.value} textTransform="capitalize">{plot?.name ? String(plot?.name).charAt(0).toUpperCase() + String(plot?.name).slice(1) : "Not available"}</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.label} textTransform="capitalize">Plot Size</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.value} textTransform="capitalize">{plot?.plotSize ? String(plot?.plotSize).charAt(0).toUpperCase() + String(plot?.plotSize).slice(1) : "Not available"}</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.label} textTransform="capitalize">Location</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.value} textTransform="capitalize">{plot?.location ? String(plot?.location).charAt(0).toUpperCase() + String(plot?.location).slice(1) : "Not available"}</Typography></Grid>
                                               {/* <Grid item xs={6}><Typography sx={styles.label} textTransform="capitalize">Days</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.value} textTransform="capitalize">{plot?.days ? String(plot?.days).charAt(0).toUpperCase() + String(plot?.days).slice(1) : "Not available"}</Typography></Grid> */}
                                                <Grid item xs={6}><Typography sx={styles.label} textTransform="capitalize">Square Feet</Typography></Grid>
                                                <Grid item xs={6}><Typography sx={styles.value} textTransform="capitalize">{plot?.squareFeet ? String(plot?.squareFeet).charAt(0).toUpperCase() + String(plot?.squareFeet).slice(1) : "Not available"}</Typography></Grid>
                                            </Grid>
                                            <Box sx={{ mt: 2 }}>
                                                <Typography sx={{ fontSize: { xs: "20px", sm: "20px", md: "20px" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500, textTransform: "capitalize" }}>Requirements</Typography>
                                                <FormGroup>
                                                    {services.length > 0 ? (
                                                        services.map((service: any) => (
                                                            <Box key={service.id} sx={{ gap: 1, display: "flex", alignItems: "center", margin: "-3px" }}>
                                                                <Checkbox sx={{ color: "black", "&.Mui-checked": { color: "black" }, cursor: "default" }} checked disabled />
                                                                <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" }, color: "#212121", ml: "-3px", textTransform: "capitalize" }}>
                                                                    {service.remarks || service.servicesReq?.name || service.description || "No requirements"}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography sx={{ textTransform: "capitalize" }}>No requirements available</Typography>
                                                    )}
                                                </FormGroup>
                                        
                                                {Array.isArray(location.state?.unselected) && location.state.unselected.length > 0 && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <FormGroup>
                                                            {location.state.unselected.map((service: any) => (
                                                                <Box key={service.id} sx={{ gap: 1, display: "flex", alignItems: "center", margin: "-3px" }}>
                                                                    <Checkbox sx={{ color: "black", "&.Mui-checked": { color: "black" }, cursor: "default" }} checked={false} disabled />
                                                                    <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" }, color: "#212121", ml: "-3px", textTransform: "capitalize" }}>
                                                                        {service.name || "No requirements"}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </FormGroup>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ px: { xs: 2, sm: 3, md: 3, lg: 7, xl: 10 } }}>
                                    <Typography sx={{ fontSize: { xs: "20px", sm: "20px", md: "20px" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500, mb: 1, textTransform: "capitalize" }}>Images</Typography>
                                    <Grid container spacing={2}>
                                        {files.length > 0 ? (
                                            files.map((file: any) => (
                                                <Grid item xs={6} sm={6} md={3} lg={3} key={file.id}>
                                                    <Box component="img" src={file.fileUrl || CustomerReqImg} alt={` No images available`} sx={{ width: "100%", borderRadius: "8px", height: "200px", objectFit: "cover", mb: 4 }} />
                                                </Grid>
                                            ))
                                        ) : (
                                            <Grid item xs={6} sm={6} md={3} lg={3}>
                                                <Box sx={{ mb: 2, width: "100%", height: "200px", border: "1px solid #ccc", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f9f9f9" }}>
                                                    <Typography sx={{ textTransform: "capitalize", fontSize: "16px", color: "#666", textAlign: "center" }}>No images available</Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                    {/* <Grid container spacing={1} sx={{ mt: 2, mb: 1 }}>
                                        <Grid item xs={6}><Typography sx={styles.label}>Contractor</Typography></Grid>
                                        <Grid item xs={6}><Typography sx={styles.value}>{contractor?.name || "Not available"}</Typography></Grid>
                                    </Grid> */}
                                </Box>
                                <Box sx={{ px: { xs: 2, sm: 3, md: 3, lg: 7, xl: 10 }, mt: 2 }}>
                                    <Typography sx={{ fontSize: { xs: "20px", sm: "20px", md: "20px" }, fontWeight: 500, mb: 2, textTransform: "capitalize" }}>Contractor Plans</Typography>
                                    <Grid container spacing={2}>
                                        {pricingPlans.length > 0 ? (
                                            pricingPlans.map((plan: any, planIndex: number) => (
                                                <Grid item xs={12} sm={6} md={4} lg={4} key={planIndex}>
                                                    <Card 
                                                        elevation={0} 
                                                        sx={{ 
                                                            border: "0.6px solid #161717", 
                                                            borderRadius: "10px",
                                                            minHeight: "450px",
                                                            display: "flex",
                                                            flexDirection: "column"
                                                        }}
                                                    >
                                                        <CardContent sx={{ 
                                                            height: "100%",
                                                            display: "flex", 
                                                            flexDirection: "column",
                                                            justifyContent: "space-between",
                                                            padding: "16px"
                                                        }}>
                                                  
                                                            <Box>
                                                                <Typography sx={{ 
                                                                    fontSize: '20px', 
                                                                    fontWeight: 600, 
                                                                    border: "0.2px solid #161717", 
                                                                    maxWidth: 'fit-content', 
                                                                    padding: "1px 25px", 
                                                                    borderRadius: '20px', 
                                                                    margin: '0 auto', 
                                                                    textTransform: 'capitalize',
                                                                    mb: 2
                                                                }}>
                                                                    {plan.name}
                                                                </Typography>
                                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                                    <Typography sx={{ fontSize: '40px', fontWeight: 600, textAlign: 'center' }}>
                                                                        ₹{getAdjustedPrice(plan.price)}
                                                                    </Typography>
                                                                    <Typography sx={{ 
                                                                        fontSize: '20px', 
                                                                        fontWeight: 500, 
                                                                        textAlign: 'center', 
                                                                        textTransform: 'capitalize',
                                                                        mb: 2
                                                                    }}>
                                                                        Per Square Feet
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            
                           
                                                            <Box sx={{ 
                                                                flexGrow: 1, 
                                                                display: "flex", 
                                                                flexDirection: "column",
                                                                justifyContent: "center"
                                                            }}>
                                                                {plan.features && plan.features.length > 0 ? (
                                                                    <>
                                                                        <Box sx={{ mb: 2 }}>
                                                                            {(expandedPlanIndex === planIndex ? plan.features : plan.features.slice(0, 4)).map((feature: any, idx: number) => (
                                                                                <Typography 
                                                                                    key={idx} 
                                                                                    sx={{ 
                                                                                        fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' }, 
                                                                                        fontWeight: 400, 
                                                                                        textAlign: 'start', 
                                                                                        display: 'flex', 
                                                                                        gap: { xs: '8px', sm: '7px', md: '7px', lg: '8px', xl: '9px' }, 
                                                                                        textTransform: 'capitalize',
                                                                                        mb: 1,
                                                                                        alignItems: 'center'
                                                                                    }}
                                                                                >
                                                                                    <CheckCircleIcon sx={{ fontSize: '18px', color: '#000' }} />
                                                                                    {feature.name}
                                                                                </Typography>
                                                                            ))}
                                                                        </Box>
                                                                        {plan.features.length > 4 && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
                                                                                <Button
                                                                                    onClick={() => handleToggleFeatures(planIndex)}
                                                                                    sx={{
                                                                                        textTransform: 'none',
                                                                                        color: '#89652A',
                                                                                        fontSize: '14px',
                                                                                        fontWeight: 500,
                                                                                        '&:hover': {
                                                                                            backgroundColor: 'rgba(137, 101, 42, 0.1)'
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {expandedPlanIndex === planIndex ? 'View Less' : 'View More'}
                                                                                </Button>
                                                                            </Box>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <Typography sx={{ 
                                                                        fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' }, 
                                                                        fontWeight: 400, 
                                                                        textAlign: 'center', 
                                                                        textTransform: 'capitalize',
                                                                        color: '#666'
                                                                    }}>
                                                                        No features available
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))
                                        ) : (
                                            <Typography sx={{ textAlign: 'center', width: '100%', textTransform: 'capitalize' }}>No plans available</Typography>
                                        )}
                                    </Grid>

                                    {pricingPlans.length > 0 && !loadingMargin && marginConfig && (
                                        <Grid container spacing={2} sx={{ mt: 4, mb: 4, width: '100%', mx: 'auto' }}>

                                            <Grid item xs={6} sm={6} display="flex" alignItems="center">
                                                <Typography sx={{ fontSize: { xs: '18px', sm: '18px' }, fontWeight: 500, color: '#212121', textTransform: 'capitalize' }}>
                                                    Mark Up
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={6} sm={6} display="flex" >
                                                {isEditingMarkUp ? (
                                                    <TextField
                                                        value={markUp}
                                                        onChange={handleMarkUpChange}
                                                        size="small"
                                                        variant="outlined"
                                                        autoFocus
                                                        sx={{
                                                            width: 100,
                                                            mr: 1,
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: '12px',
                                                                bgcolor: '#f5f5f5',
                                                                fontWeight: 600,
                                                                fontSize: 18,
                                                                padding: '2px 8px',
                                                                borderColor: '#89652A',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#B69D74',
                                                                    borderWidth: '1.5px',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#89652A',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#89652A',
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                          
                                                        }}
                                                        inputProps={{
                                                            maxLength: 6,
                                                        }}
                                                    />

                                                ) : (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 18,
                                                            fontWeight: 600,
                                                            color: '#212121',
                                                            mr: 1,
                                                            minWidth: 40,
                                                            textAlign: 'right',
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {markUp}
                                                    </Typography>
                                                )}
                                                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#212121', mr: 1, textTransform: 'capitalize' }}>
                                                    {marginConfig.isPercent ? '%' : ''}
                                                </Typography>
                                                {!isEditingMarkUp ? (
                                                    <EditIcon sx={{ fontSize: 20, color: '#89652A', cursor: 'pointer' }} onClick={handleEditMarkUp} />
                                                ) : (
                                                    <CheckIcon sx={{ fontSize: 20, color: '#89652A', cursor: 'pointer' }} onClick={handleSaveMarkUp} />
                                                )}
                                            </Grid>
                                        </Grid>
                                    )}

                                </Box>
                                <Box sx={styles.submitContainer}>
                                    {pricingPlans.length > 0 ? (
                                        <Button sx={{ ...styles.submitButton, textTransform: 'capitalize' }} onClick={handleSendEstimate} disabled={isLoading}>
                                            {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Estimate'}
                                        </Button>
                                    ) : (
                                        <Button sx={{ ...styles.submitButton, textTransform: 'capitalize' }} onClick={() => navigate(-1)}>
                                            Back to List
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Footer />
            </Box>
        </>
    );
};

export default EstimateSection;


const styles = {
    mainContainer: {
        borderRadius: { xs: "10px", sm: "10px" },
        boxShadow: '0px 0px 3px 0px #00000033',
    },
    header: {
        background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)",
        color: "#fff",
        padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" },
        borderTopLeftRadius: { xs: "10px", sm: "10px" },
        borderTopRightRadius: { xs: "10px", sm: "10px" }
    },
    headerTypography: {
        fontSize: { xs: '20px', sm: '25px', md: '25px' },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 500,
        lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
        color: 'white'
    },
    mainBox: {
        px: { xs: '13px', sm: '20px', md: '30px', lg: '50px' },
        py: { xs: '13px', sm: '15px', md: '20px', lg: '20px' },
    },
    borderBox: {
        borderRadius: '10px',
        border: '0.75px solid #2D2D2D33',
        mb: 4,
    },
    label: {
        fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' },
        fontWeight: { xs: 500, sm: 500, md: 500, lg: 500 },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    value: {
        fontWeight: { xs: 500, sm: 500, md: 500, lg: 500 },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontSize: { xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px' },
    },
    submitContainer: {
        display: 'flex',
        justifyContent: 'center',
        mb: 6,
        mt: 3
    },
    submitButton: {
        textTransform: 'capitalize',
        fontSize: { xs: '16px', md: '16px' },
        fontWeight: 600,
        borderRadius: '28px',
        color: 'white',
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        bgcolor: '#89652A',
        py: 0.9,
        px: { xs: 4, md: 6 }
    }
};